import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import { Button, Container, Modal, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LoadStatus } from '../../constants/constants';
import {
  AssessmentType,
  createCopy,
  selectAssessmentLoadStatus,
  selectSelectedAssessment,
} from '../../redux/reducers/assessmentReducer';
import { selectFrameworkTitle } from '../../redux/reducers/frameworkReducer';
import LoadingIndicator from '../LoadingIndicator';

function validate(details) {
  return details.name !== '' && details.description !== '';
}

function CopyAssessmentModal({ handleClose }) {
  const initialAssessmentDetails = {
    name: '',
    assessmentType: AssessmentType.AUDIT,
    frameworkId: '',
    description: '',
  };
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const baseAssessment = useSelector(selectSelectedAssessment);
  const frameworkTitle = useSelector((state) => selectFrameworkTitle(state, baseAssessment.frameworkId ?? ''));
  const loadStatus = useSelector(selectAssessmentLoadStatus);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [assessmentDetails, setAssessmentDetails] = useState(initialAssessmentDetails);
  const [validationError, setValidationError] = useState(false);

  const handleInitialize = () => {
    setAssessmentDetails({ ...initialAssessmentDetails });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const newDetails = { ...assessmentDetails };
    newDetails[name] = value;
    setAssessmentDetails(newDetails);
    setUnsavedChanges(true);
  };

  const handleSave = () => {
    if (validate(assessmentDetails)) {
      dispatch(createCopy(assessmentDetails));
      handleClose();
    } else {
      setValidationError(true);
    }
  };

  const handleUnsavedChanges = (_, reason) => {
    if (unsavedChanges) {
      switch (reason) {
        case 'backdropClick':
          // Prevent outside click modal close
          break;
        default:
          setUnsavedChanges(false);
          handleInitialize();
          handleSave();
          break;
      }
    } else {
      handleInitialize();
      handleClose();
    }
  };

  const handleCancel = () => {
    handleInitialize();
    handleClose();
  };

  useEffect(() => {
    setAssessmentDetails({
      ...initialAssessmentDetails,
      assessmentType: baseAssessment.assessmentType,
      frameworkId: baseAssessment.frameworkId,
    });
  }, [baseAssessment]);

  return (
    <Modal open onClose={handleUnsavedChanges}>
      {loadStatus === LoadStatus.SUCCESS ? (
        <Container variant="modal" disableGutters sx={{ width: '600px' }}>
          <Stack variant="modalTop" spacing={2}>
            <Typography variant="h6">{t('assessment.newAssessmentAsCopy')}</Typography>
            <Typography>{t('assessment.newAssessmentAsCopyDiscription')}</Typography>
            <TextField
              required
              variant="outlined"
              subtype="signinField"
              onChange={handleChange}
              name="name"
              InputLabelProps={{
                shrink: true,
              }}
              label={t('assessment.modal.name')}
              value={assessmentDetails.name}
            />
            {validationError && <Typography variant="error">{t('error.emptyField')}</Typography>}
            <TextField
              required
              variant="outlined"
              subtype="signinField"
              onChange={handleChange}
              name="description"
              InputLabelProps={{
                shrink: true,
              }}
              label={t('assessment.modal.description')}
              multiline
              rows={6}
              value={assessmentDetails.description}
            />
            <Stack>
              <Typography>{t('assessment.modal.type')}</Typography>
              <Typography variant="body2">{t(`assessment.type.${baseAssessment.assessmentType}`)}</Typography>
            </Stack>
            <Stack>
              <Typography>{t('assessment.modal.framework.title')}</Typography>
              <Typography variant="body2">{frameworkTitle}</Typography>
            </Stack>
          </Stack>
          <Stack variant="modalButtons" direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="text" startIcon={<ClearIcon />} onClick={() => handleCancel()}>
              {t('assessment.modal.buttonCancel')}
            </Button>
            <Button
              variant="contained"
              disabled={!assessmentDetails.name || !assessmentDetails.description}
              startIcon={<SaveIcon />}
              onClick={() => handleSave()}
            >
              {t('assessment.modal.buttonSaveNew')}
            </Button>
          </Stack>
        </Container>
      ) : (
        <LoadingIndicator />
      )}
    </Modal>
  );
}

export default CopyAssessmentModal;
